import { useParams, useLocation } from 'react-router-dom';
import {
  doApiCheck,
  getDirectoryGroupQuery,
  groupSecuritySettingsQuery,
  UserKeys,
} from '../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import GroupSecuritySettingsForm from './group-security-settings-form';
import { useUserSettings } from '../../../utils/use-user-settings';
import { userPortalPagesConfig } from '../../../shared/user-portal-pages-config';

import ScopesAlert from '../../../shared/scopes-alert';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import SharedButton from '../../../shared/buttons/shared-button';

export default function GroupSecuritySettings() {
  const { groupEmail } = useParams() as { groupEmail: string };
  const { changeLogRoles, directoryRoles } = useUserSettings();
  const location = useLocation();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );

  const {
    data: group,
    isLoading: isGroupLoading,
    isFetching: isGroupFetching,
  } = useQuery(getDirectoryGroupQuery(groupEmail));

  const { data: accessGranted, isLoading: checkingApiAccess } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'cloudIdentity'],
    queryFn: () => doApiCheck('cloudIdentity', null),
    staleTime: 0,
  });

  const {
    data: groupSecuritySettings,
    isLoading: isSecurityLoading,
    isFetching: isSecurityFetching,
  } = useQuery({
    ...groupSecuritySettingsQuery(groupEmail, group?.id ?? ''),
    enabled: !!accessGranted && group !== undefined,
  });

  const isLoading =
    isSecurityLoading ||
    isGroupLoading ||
    isSecurityFetching ||
    isGroupFetching;

  if (!accessGranted && !checkingApiAccess) {
    return <ScopesAlert apiKey="cloudIdentity" />;
  }

  if (
    groupSecuritySettings !== undefined &&
    group !== undefined &&
    !isLoading
  ) {
    return (
      <GroupSecuritySettingsForm
        groupSecuritySettings={groupSecuritySettings}
        groupId={group?.id ?? ''}
        userView={userView}
        changeLogRoles={changeLogRoles}
        isLoading={isLoading}
        groupEmail={groupEmail}
        directoryRoles={directoryRoles}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: 'absolute', top: -60, right: 0 }}
      >
        <SharedIconButton variant="refresh" tooltipLabel="Refresh" />
        <SharedButton variant="primary" disabled>
          Save
        </SharedButton>
      </Stack>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          margin: '8px 0',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>
            Evaluation state
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', pt: 1, pl: 4 }}>
            <Skeleton
              variant="circular"
              width={20}
              height={20}
              sx={{ mr: 1 }}
            />
            <Skeleton variant="text" width={100} height={20} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 300, pt: 2, pl: 4 }}>
            <Skeleton variant="text" width="90%" height={20} />
          </Typography>
        </Box>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={220}
          sx={{ mt: 4, borderRadius: 1 }}
        />
      </Box>
    </Box>
  );
}
